import React, { useState, useRef } from "react";
import { Card, Dropdown } from "react-bootstrap";
import PlantedAcresForm from "./PlantedAcresForm";
import Timeline from "./Timeline";
import Notes from "components/common/notes/Notes";
import ConfirmationPopup from "components/common/ConfirmationPopup";
import { useDispatch, useSelector } from "react-redux";
import {
  setEditedPlantedAcre,
  setPlantedAcres,
  setSelectedPlantedAcre,
} from "reduxStore/slices/plantedAcresSlice";
import PlantedAcresService from "services/PlantedAcresService";
import {
  PLANTED_ACRES_DELETE_ERROR,
  ARE_YOU_SURE_DELETE_PLANTED_ACRES,
  PLANTED_ACRES_HAS_BEEN_DELETED_SUCCESSFULLY,
} from "configurations/MessageConstants";
import {
  MODE_TYPES,
  NOTES_TYPES,
  PLANTING_STATUS,
} from "configurations/Constants";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

const PlantedAcresCard = ({
  type,
  plantedAcre,
  handlePlantedAcreClick,
  onEditPlantedAcre,
  onShowTimeline,
  onShowNotes,
  onDeletePlantedAcre,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const plantedAcres = useSelector((state) => state.plantedAcres.plantedAcres);
  const [showPlantedAcresForm, setShowPlantedAcresForm] = useState(false);
  const [showTimeline, setShowTimeline] = useState(false);
  const [showNotes, setShowNotes] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const selectedSite = useSelector((state) => state.site.selectedSite);
  if (!plantedAcre) {
    return null;
  }
  const isSharedViewOnly = selectedSite.isShared && !selectedSite.editor;

  const handleDeleteClick = () => {
    if (onDeletePlantedAcre) {
      onDeletePlantedAcre(plantedAcre);
    } else {
      setShowDeleteConfirmation(true);
    }
  };

  const handleConfirmDelete = async () => {
    setIsDeleting(true);
    try {
      await PlantedAcresService.deletePlantedAcres(
        selectedSite.id,
        plantedAcre.id
      );
      setShowDeleteConfirmation(false);
      dispatch(
        setPlantedAcres(
          plantedAcres.filter((item) => item.id !== plantedAcre.id)
        )
      );
      dispatch(setSelectedPlantedAcre(null));
      navigate(`/sites/${selectedSite.id}/planted-acres`, {
        state: { fromBack: true },
      });
      toast.success(PLANTED_ACRES_HAS_BEEN_DELETED_SUCCESSFULLY);
    } catch (error) {
      console.error(PLANTED_ACRES_DELETE_ERROR, error);
      toast.error(error.response.data.detail);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditClick = () => {
    dispatch(setEditedPlantedAcre(plantedAcre));
    if (onEditPlantedAcre) {
      onEditPlantedAcre(plantedAcre);
    } else {
      setShowPlantedAcresForm(true);
    }
  };

  const handleShowNotes = () => {
    if (onShowNotes) {
      onShowNotes(plantedAcre);
    } else {
      setShowNotes(true);
    }
  };

  const handlePlantedAcreCardClick = (e) => {
    if (dropdownRef.current?.contains(e.target)) return;
    handlePlantedAcreClick(plantedAcre);
  };

  return (
    <>
      <Card className={type} onClick={handlePlantedAcreCardClick}>
        <Card.Body className="p-2">
          <div className="d-flex justify-content-between">
            <div className="text-truncate">
              <div className="text-truncate">{plantedAcre.name}</div>
              <small className="text-muted d-flex gap-1 align-items-center">
                <span>{plantedAcre.cropName}</span>|
                <span className="text-truncate">{plantedAcre.seedName}</span>
              </small>
            </div>
            <div className="ms-2 d-flex flex-column align-items-end">
              <small className="text-muted text-nowrap">
                {plantedAcre.acres} Acres
              </small>
              <small className="text-muted text-nowrap">
                {plantedAcre.tillageName}
              </small>
            </div>
          </div>
          <div className="d-flex justify-content-between align-items-center">
            <div
              className={`planted-date-badge ${plantedAcre.status.toLowerCase()} text-muted`}
            >
              <small>
                {plantedAcre.status} - {plantedAcre.eventDate}
              </small>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <Dropdown ref={dropdownRef} align="end">
                <Dropdown.Toggle
                  variant="outline-dark"
                  className="no-caret"
                  size="sm"
                  id={`dropdown-${plantedAcre.name}`}
                  aria-label="Planted Acres actions"
                >
                  <i className="fa fa-ellipsis-h" aria-hidden="true"></i>
                </Dropdown.Toggle>
                <Dropdown.Menu style={{ minWidth: "fit-content" }}>
                  <Dropdown.Item onClick={handleEditClick}>
                    {isSharedViewOnly ||
                    plantedAcre.status === PLANTING_STATUS.FAILED ||
                    plantedAcre.status === PLANTING_STATUS.HARVESTED ? (
                      <>
                        <i className="fa fa-eye me-2" aria-hidden="true"></i>
                        View
                      </>
                    ) : (
                      <>
                        <i className="fa fa-edit me-2" aria-hidden="true"></i>
                        Edit
                      </>
                    )}
                  </Dropdown.Item>
                  <Dropdown.Item
                    onClick={() => {
                      if (onShowTimeline) {
                        onShowTimeline(plantedAcre);
                      } else {
                        setShowTimeline(true);
                      }
                    }}
                  >
                    <i className="fa fa-clock me-2" aria-hidden="true"></i>
                    Timeline
                  </Dropdown.Item>
                  {!isSharedViewOnly && (
                    <>
                      <Dropdown.Item onClick={handleShowNotes}>
                        <i
                          className="fa fa-sticky-note me-2"
                          aria-hidden="true"
                        ></i>
                        Notes
                      </Dropdown.Item>
                      <Dropdown.Item onClick={handleDeleteClick}>
                        <i className="fa fa-trash me-2" aria-hidden="true"></i>
                        Delete
                      </Dropdown.Item>
                    </>
                  )}
                </Dropdown.Menu>
              </Dropdown>
            </div>
          </div>
        </Card.Body>
      </Card>
      {!onEditPlantedAcre && (
        <PlantedAcresForm
          mode={MODE_TYPES.EDIT}
          show={showPlantedAcresForm}
          onClose={() => setShowPlantedAcresForm(false)}
        />
      )}

      {!onShowTimeline && showTimeline && (
        <Timeline
          show={showTimeline}
          handleClose={() => setShowTimeline(false)}
          plantedAcresId={plantedAcre.id}
        />
      )}

      {!onShowNotes && showNotes && (
        <Notes
          id={plantedAcre.id}
          type={NOTES_TYPES.PLANTED_ACRES}
          name={plantedAcre.name}
          show={showNotes}
          handleClose={() => setShowNotes(false)}
        />
      )}

      {!onDeletePlantedAcre && (
        <ConfirmationPopup
          show={showDeleteConfirmation}
          title="Delete Planted Acres"
          message={
            <span className="text-muted">
              {ARE_YOU_SURE_DELETE_PLANTED_ACRES}{" "}
              <strong>{plantedAcre.name}</strong>?
            </span>
          }
          onConfirm={handleConfirmDelete}
          onCancel={() => setShowDeleteConfirmation(false)}
          confirmText="Delete"
          cancelText="Cancel"
          isDeleting={isDeleting}
        />
      )}
    </>
  );
};

export default PlantedAcresCard;
